/**
 * Bounded pending state for task commands (IR-126 §7.2, phase 3.2d).
 *
 * Every mutation is queued and rendered optimistically, so the value on screen
 * runs ahead of the server by a second or two. That is the point — but it means
 * **an edit that looks saved but was not is the worst failure this feature can
 * produce** (§7.2), and the only defence is that the screen says which of the
 * two it is looking at.
 *
 * `submitTaskCommand` already bounds the wait: after
 * {@link COMMAND_TIMEOUT_MS} with no terminal status it resolves `unresolved`
 * rather than hanging. What it cannot do is *say so anywhere*. Until this
 * module, that outcome was returned to a caller, written to the log, and
 * otherwise indistinguishable on screen from success — the silent success §7.2
 * forbids in as many words.
 *
 * ## Two states, and why the second one is not an error
 *
 * - **saving** — a command is in flight. Ordinary, brief, and worth showing
 *   only so that a card sliding back later is not a surprise (§7.2.5).
 * - **unconfirmed** — the timeout elapsed. We stopped watching; the command may
 *   still apply. This is **not** a failure and must not be styled as one: the
 *   edit is probably fine, and the honest claim is that we cannot say.
 *
 * A `discarded` or `failed` outcome clears pending state without leaving
 * anything behind, because both have already rolled the value back — the screen
 * is showing the truth again, and 3.3 owns what to say about the failure.
 *
 * ## Why `unconfirmed` clears itself
 *
 * A warning that never leaves is its own kind of lie: it keeps claiming
 * uncertainty long after the answer arrives. So it clears when the task's
 * `last_revision_seq` moves past the seq the unresolved command was composed
 * against — at that point the board is showing a state newer than the one in
 * doubt, and the user can see the truth for themselves.
 *
 * That is deliberately "the task moved on", not "our command applied": a
 * teammate's edit clears it too. Distinguishing them would need the revision
 * trail (5.4), and the weaker claim is the one this module can actually make.
 */
import { reactive } from 'vue'
import { submitSequencedCommand } from './useTaskSeq'
import type { SequencedOutcome, SequencedPayload } from './useTaskSeq'
import type { SubmitOptions } from './useTaskCommands'

export type PendingPhase = 'saving' | 'unconfirmed'

export interface TaskPendingState {
  phase: PendingPhase
  /** How many commands are in flight. Zero when the phase is `unconfirmed`. */
  inFlight: number
}

interface PendingEntry {
  inFlight: number
  /**
   * The `base_seq` of a command that timed out, if any.
   *
   * Held rather than a boolean because clearing needs a threshold: the state
   * ends when the task moves past this, not when someone decides enough time
   * has passed.
   */
  unconfirmedAt?: number
}

const pending = reactive(new Map<string, PendingEntry>())

const key = (projectId: string, taskId: string) => `${projectId}/${taskId}`

/**
 * What to show for a task, or `undefined` when there is nothing to say.
 *
 * `saving` outranks `unconfirmed`: a command in flight now is the more current
 * fact, and showing both at once would ask the user to hold two thoughts about
 * one card.
 *
 * A **plain read**, not a `ComputedRef`. Returning a computed would bind the
 * ids at the moment it was called, so a component that re-uses one instance for
 * a different task — a list without a stable `:key`, a card whose props change —
 * would keep watching the task it first mounted with. Reading the reactive map
 * inside the caller's own `computed(() => taskPending(...))` tracks the ids as
 * dependencies too, so the answer follows the props.
 */
export function taskPending(projectId: string, taskId: string): TaskPendingState | undefined {
  const entry = pending.get(key(projectId, taskId))
  if (!entry) return undefined
  if (entry.inFlight > 0) return { phase: 'saving', inFlight: entry.inFlight }
  if (entry.unconfirmedAt !== undefined) return { phase: 'unconfirmed', inFlight: 0 }
  return undefined
}

/**
 * Clear an `unconfirmed` state once the task has moved past it.
 *
 * Call whenever a task's `last_revision_seq` changes — the snapshot listener is
 * the natural place. Cheap and idempotent for tasks with nothing pending.
 */
export function reconcilePending(projectId: string, taskId: string, serverSeq: number): void {
  const k = key(projectId, taskId)
  const entry = pending.get(k)
  if (!entry || entry.unconfirmedAt === undefined) return
  if (serverSeq <= entry.unconfirmedAt) return

  if (entry.inFlight > 0) {
    pending.set(k, { inFlight: entry.inFlight })
    return
  }
  pending.delete(k)
}

/** Drop the `unconfirmed` notice because the user acknowledged it. */
export function dismissPending(projectId: string, taskId: string): void {
  const k = key(projectId, taskId)
  const entry = pending.get(k)
  if (!entry) return
  if (entry.inFlight > 0) {
    pending.set(k, { inFlight: entry.inFlight })
    return
  }
  pending.delete(k)
}

/**
 * Show `saving` for a command this tab is waiting on but did not sequence.
 *
 * Two callers, and neither goes through `submitPendingCommand`:
 *
 * - **recovery (3.2c)** — the command was written before the page reloaded, so
 *   there is no submission here, only a re-attach.
 * - **`task_create` (4.1a)** — there is a submission, but no `base_seq` to
 *   anchor it to (§4.3), so the sequenced path does not apply.
 *
 * Named for the job rather than the first caller: this was `markRecovered`
 * until a second caller made the name wrong.
 */
export function markPending(projectId: string, taskId: string): void {
  enter(projectId, taskId)
}

/**
 * Stop showing `saving` for such a command.
 *
 * `unconfirmedAt` is what `leave` compares the server's seq against, and both
 * callers may legitimately have nothing to pass: a recovered timeout we cannot
 * anchor, or a create that never had a `base_seq`. Omitted, the task reads as
 * `unconfirmed` on the same terms as any other timeout — we stopped watching,
 * which is not the same as knowing it failed.
 */
export function releasePending(
  projectId: string,
  taskId: string,
  _commandId: string,
  unconfirmedAt?: number,
): void {
  leave(projectId, taskId, unconfirmedAt)
}

/**
 * The `(projectId, taskId)` a Firestore task path maps to.
 *
 * One helper rather than the same two-line split at every render site, because
 * the second half is not obvious: for a **proposal** the id taken here is the
 * proposal's own document id, and that is deliberate — `apply_create` gives the
 * task it creates the proposal's id (§7.3). So an accept's pending state is
 * keyed on the row the user is looking at *and* on the task that will exist,
 * which are the same string. Split it by hand in two places and one of them
 * eventually keys on something else.
 *
 * Returns `undefined` for anything that is not a task document, so a caller can
 * render nothing rather than key on the wrong id.
 */
export function pendingKeyFor(
  taskPath: string | null | undefined,
): { projectId: string; taskId: string } | undefined {
  if (!taskPath) return undefined
  const segments = taskPath.split('/')

  // Checked by SHAPE, not by arithmetic. A parity test ("even segment count")
  // reads like validation and is not: `projects/{pid}/assessments/{aid}` is four
  // segments and even, so it passed — keying an assessment id as a task id,
  // silently, on any surface that handed one over.
  //
  // All three real shapes end `.../tasks/{tid}`:
  //   projects/{pid}/tasks/{tid}
  //   projects/{pid}/assessments/{aid}/results/{rid}/tasks/{tid}
  //   projects/{pid}/assessments/{aid}/tasks/{tid}
  // so requiring that suffix rejects both collection paths AND documents from
  // the wrong collection, for the right reason rather than by coincidence.
  if (segments.length < 4) return undefined
  if (segments[0] !== 'projects' || segments[segments.length - 2] !== 'tasks') return undefined

  const [, projectId] = segments
  const taskId = segments[segments.length - 1]
  if (!projectId || !taskId) return undefined
  return { projectId, taskId }
}

/** Test seam. Never call from application code. */
export function _resetPending(): void {
  pending.clear()
}

function enter(projectId: string, taskId: string): void {
  const k = key(projectId, taskId)
  const entry = pending.get(k)
  pending.set(k, { inFlight: (entry?.inFlight ?? 0) + 1, unconfirmedAt: entry?.unconfirmedAt })
}

function leave(projectId: string, taskId: string, unconfirmedAt?: number): void {
  const k = key(projectId, taskId)
  const entry = pending.get(k)
  if (!entry) return

  const inFlight = Math.max(0, entry.inFlight - 1)
  // The HIGHEST unconfirmed seq wins, not the most recent one.
  //
  // Usually they are the same. They come apart after a chain void: `voidChain`
  // resets the ledger's prediction to the confirmed seq, so a command submitted
  // afterwards can carry a LOWER base_seq than one that timed out before it.
  // Take the later value and the notice clears at a threshold the earlier
  // command never needed — dropping the warning while its optimistic value is
  // still on screen. This module's whole argument is that a lingering warning
  // is a lie; clearing one early is the same lie told faster.
  const stillUnconfirmed =
    unconfirmedAt === undefined
      ? entry.unconfirmedAt
      : Math.max(unconfirmedAt, entry.unconfirmedAt ?? unconfirmedAt)

  if (inFlight === 0 && stillUnconfirmed === undefined) {
    pending.delete(k)
    return
  }
  pending.set(k, { inFlight, unconfirmedAt: stillUnconfirmed })
}

/**
 * Submit a command and keep the task's pending state in step with it.
 *
 * The outcomes map to the two states as §7.2 requires — note that only one of
 * the four leaves anything on screen:
 *
 * - `done` — applied. Clears everything, **including a prior `unconfirmed`**:
 *   the task has demonstrably moved, so the doubt is answered.
 * - `failed` / `discarded` — rolled back already. Nothing pending to show; 3.3
 *   owns the message.
 * - `unresolved` — the one this card exists for. Leaves `unconfirmed` behind,
 *   anchored to the seq it was composed against.
 *
 * A throw clears the pending state too. Whether it was ever queued is
 * `isNotQueued`'s question and the ledger's to act on; either way there is no
 * command left for *this* module to be waiting on, and leaving a spinner
 * spinning over a rejected promise would be the silent-failure shape again.
 */
export async function submitPendingCommand(
  projectId: string,
  payload: SequencedPayload,
  serverSeq: number,
  options: SubmitOptions = {},
): Promise<SequencedOutcome> {
  const taskId = payload.task_id
  enter(projectId, taskId)

  let outcome: SequencedOutcome
  try {
    outcome = await submitSequencedCommand(projectId, payload, serverSeq, options)
  } catch (e) {
    leave(projectId, taskId)
    throw e
  }

  if (outcome.status === 'unresolved') {
    leave(projectId, taskId, outcome.baseSeq)
  } else if (outcome.status === 'done') {
    // The task moved, so any earlier doubt is settled — the board is now
    // showing something newer than whatever was unconfirmed.
    const k = key(projectId, taskId)
    const entry = pending.get(k)
    if (entry) pending.set(k, { inFlight: entry.inFlight })
    leave(projectId, taskId)
  } else {
    leave(projectId, taskId)
  }

  return outcome
}
